import React, { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { Appointment } from '../../services/appointmentService';
import { AppointmentType } from '../../services/appointmentTypeService';
import { darkenColor, getAppointmentColor, getAppointmentTextColor } from '../../utils/calendarColorUtils';

interface CalendarPreviewPanelProps {
    /** The date to display in this preview panel */
    date: Date;
    /** Appointments to render */
    appointments: Appointment[];
    /** Appointment types for color resolution */
    appointmentTypes: AppointmentType[];
    /** The FullCalendar view type to use (e.g. 'listWeek', 'timeGridDay') */
    viewType: string;
    /** First day of week (0=Sunday, 1=Monday, etc.) */
    firstDayOfWeek: number;
    /** Whether to restrict to business hours */
    restrictToBusinessHours?: boolean;
    /** Business hours start time (e.g. '08:00:00') */
    slotMinTime?: string;
    /** Business hours end time (e.g. '17:00:00') */
    slotMaxTime?: string;
}

/**
 * A read-only FullCalendar instance used as a swipe preview panel.
 * Renders identically to the main calendar since it uses the same FullCalendar component.
 */
const CalendarPreviewPanel: React.FC<CalendarPreviewPanelProps> = ({
    date,
    appointments,
    appointmentTypes,
    viewType,
    firstDayOfWeek,
    slotMinTime = '00:00:00',
    slotMaxTime = '24:00:00'
}) => {
    const events = useMemo(() => {
        return appointments.map(appointment => {
            let endTime = appointment.endTime;
            if (appointment.isAllDay) {
                const endDate = new Date(appointment.endTime);
                endDate.setDate(endDate.getDate() + 1);
                endTime = endDate.toISOString();
            }

            return {
                id: appointment.id,
                title: appointment.description,
                start: appointment.startTime,
                end: endTime,
                allDay: appointment.isAllDay,
                backgroundColor: getAppointmentColor(appointment, appointmentTypes),
                textColor: getAppointmentTextColor(appointment, appointmentTypes),
                extendedProps: {
                    description: appointment.description,
                    appointmentTypeId: appointment.appointmentTypeId,
                    appointment: appointment,
                    customerName: appointment.customerName,
                    customerEmail: appointment.customerEmail,
                    customerPhone: appointment.customerPhone
                }
            };
        });
    }, [appointments, appointmentTypes]);

    return (
        <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView={viewType}
            initialDate={date}
            headerToolbar={false}
            height="100%"
            slotLabelFormat={{
                hour: 'numeric',
                minute: '2-digit',
                omitZeroMinute: true,
                meridiem: 'short'
            }}
            eventTimeFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short'
            }}
            editable={false}
            selectable={false}
            dayMaxEvents={true}
            weekends={true}
            nextDayThreshold="00:00:00"
            fixedWeekCount={false}
            forceEventDuration={true}
            nowIndicator={false}
            timeZone="local"
            firstDay={firstDayOfWeek}
            slotMinTime={slotMinTime}
            slotMaxTime={slotMaxTime}
            events={events}
            eventContent={(arg) => {
                const appointment = arg.event.extendedProps.appointment as Appointment;
                const customerName = appointment?.customerName;
                const customerPhone = appointment?.customerPhone;
                const address = appointment?.address;
                const description = appointment?.description;
                const hasCustomer = appointment?.customerId;
                const isAllDay = arg.event.allDay;

                const shouldShowTime = !(arg.view.type === 'listWeek');

                const startTime = shouldShowTime && !isAllDay && arg.event.start ? new Date(arg.event.start).toLocaleTimeString('en-GB', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }) : '';

                const ellipsisStyle = "overflow: hidden; text-overflow: ellipsis; white-space: nowrap;";
                const multiLineStyle = "overflow: hidden; word-wrap: break-word; overflow-wrap: break-word;";

                if (hasCustomer && customerName) {
                    return {
                        html: `
                            <div style="padding: 2px 1px; line-height: 1.25; height: 100%; overflow: hidden;">
                                ${startTime ? `<div style="font-size: 10px;">${startTime}</div>` : ''}
                                <div style="font-size: 0.82em; ${ellipsisStyle}">${customerName}</div>
                                ${customerPhone ? `<div style="font-size: 0.82em; opacity: 0.9; ${ellipsisStyle}">${customerPhone}</div>` : ''}
                                ${address ? `<div style="font-size: 0.82em; opacity: 0.8; ${ellipsisStyle}">${address}</div>` : ''}
                                ${description ? `<div style="font-size: 0.82em; opacity: 0.8; ${ellipsisStyle}">${description}</div>` : ''}
                            </div>
                        `
                    };
                }

                return {
                    html: `
                        <div style="padding: 2px 1px; line-height: 1.25; height: 100%; overflow: hidden; display: flex; flex-direction: column;">
                            ${startTime ? `<div style="font-size: 10px;">${startTime}</div>` : ''}
                            <div style="font-size: 0.82em; ${multiLineStyle}">${description || arg.event.title}</div>
                        </div>
                    `
                };
            }}
            eventDidMount={(arg) => {
                const appointment = arg.event.extendedProps.appointment as Appointment;
                if (appointment) {
                    const backgroundColor = getAppointmentColor(appointment, appointmentTypes);
                    const textColor = getAppointmentTextColor(appointment, appointmentTypes);
                    const borderColor = darkenColor(backgroundColor, 30);
                    arg.el.style.backgroundColor = backgroundColor;
                    arg.el.style.color = textColor;
                    arg.el.style.border = `1px solid ${borderColor}`;
                }
            }}
        />
    );
};

export default React.memo(CalendarPreviewPanel);
